import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { GET_CUSTOMER_DATA, GET_CUSTOMER_WISHLIST, REMOVE_PRODUCTS_FROM_WISHLIST, ADD_SIMPLE_PRODUCTS_TO_CART_FROM_WISHLIST, ADD_CONFIGURABLE_PRODUCTS_TO_CART_FROM_WISHLIST } from '../queries/customerQueries';
import AccountNavigation from './AccountNavigation';
import Pagination from './Pagination';
import Loader from './Loader';
import { Link } from 'react-router-dom';
import './MyAccount.css';

const AccountWishlist = () => {
  const { user, logout, isInitializing } = useAuth();
  const { cartId } = useCart();
  const [wishlistActionError, setWishlistActionError] = useState('');
  const [wishlistActionSuccess, setWishlistActionSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: customerData } = useQuery(GET_CUSTOMER_DATA, {
    skip: !user || isInitializing
  });
  const { data: wishlistData, loading: wishlistLoading, error: wishlistError, refetch: refetchWishlist } = useQuery(GET_CUSTOMER_WISHLIST, {
    variables: { currentPage, pageSize },
    skip: !user || isInitializing
  });
  const [removeProductsFromWishlist] = useMutation(REMOVE_PRODUCTS_FROM_WISHLIST);
  const [addSimpleProductsToCartFromWishlist] = useMutation(ADD_SIMPLE_PRODUCTS_TO_CART_FROM_WISHLIST);
  const [addConfigurableProductsToCartFromWishlist] = useMutation(ADD_CONFIGURABLE_PRODUCTS_TO_CART_FROM_WISHLIST);

  if (isInitializing) {
    return (
      <div className="my-account-container">
        <Loader fullScreen={true} size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-account-container">
        <div className="my-account-error">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
        </div>
      </div>
    );
  }

  const handleRemoveFromWishlist = async (wishlistId, wishlistItemId) => {
    try {
      setWishlistActionError('');
      setWishlistActionSuccess('');

      await removeProductsFromWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItemsIds: [wishlistItemId]
        }
      });

      await refetchWishlist();
      setWishlistActionSuccess('Item removed from wishlist!');
      setTimeout(() => setWishlistActionSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      setWishlistActionError(error.message || 'Failed to remove item from wishlist.');
      setTimeout(() => setWishlistActionError(''), 5000);
    }
  };

  const handleMoveToCart = async (wishlistId, wishlistItem) => {
    try {
      setWishlistActionError('');
      setWishlistActionSuccess('');

      if (!cartId) {
        setWishlistActionError('Cart not initialized. Please refresh the page and try again.');
        setTimeout(() => setWishlistActionError(''), 5000);
        return;
      }

      const isConfigurable = wishlistItem.product.__typename === 'ConfigurableProduct';

      if (isConfigurable) {
        // Check if configurable options are available
        if (!wishlistItem.configurable_options || wishlistItem.configurable_options.length === 0) {
          setWishlistActionError('Cannot add configurable product without selected options. Please remove it and add again with options selected.');
          setTimeout(() => setWishlistActionError(''), 5000);
          return;
        }

        // Add configurable product to cart
        await addConfigurableProductsToCartFromWishlist({
          variables: {
            cartId: cartId,
            cartItems: [{
              data: {
                quantity: wishlistItem.quantity,
                sku: wishlistItem.product.sku
              },
              parent_sku: wishlistItem.product.sku,
              configurable_item_options: wishlistItem.configurable_options.map(opt => ({
                option_id: opt.id,
                option_value: opt.value_id
              }))
            }]
          }
        });
      } else {
        // Add simple product to cart
        await addSimpleProductsToCartFromWishlist({
          variables: {
            cartId: cartId,
            cartItems: [{
              data: {
                quantity: wishlistItem.quantity,
                sku: wishlistItem.product.sku
              }
            }]
          }
        });
      }

      // Remove item from wishlist after successfully adding to cart
      await removeProductsFromWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItemsIds: [wishlistItem.id]
        }
      });

      await refetchWishlist();
      setWishlistActionSuccess('Item moved to cart!');
      setTimeout(() => setWishlistActionSuccess(''), 3000);
    } catch (error) {
      console.error('Error moving to cart:', error);
      setWishlistActionError(error.message || 'Failed to move item to cart.');
      setTimeout(() => setWishlistActionError(''), 5000);
    }
  };

  const wishlist = wishlistData?.customer?.wishlists?.[0];
  const wishlistItems = wishlist?.items_v2?.items || [];
  const pageInfo = wishlist?.items_v2?.page_info;
  const totalPages = pageInfo?.total_pages || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="my-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        {customerData?.customer?.firstname ? (
          <p>Welcome back, {customerData.customer.firstname}!</p>
        ) : ('')}
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="account-content">
        <AccountNavigation />
        <main className="account-main">
          <div className="account-section">
            {wishlistActionSuccess && <div className="success-message">{wishlistActionSuccess}</div>}
            {wishlistActionError && <div className="error-message">{wishlistActionError}</div>}

            {wishlistLoading ? (
              <Loader fullScreen={true} size="large" />
            ) : wishlistError ? (
              <div className="error">Error loading wishlist: {wishlistError.message}</div>
            ) : wishlistItems.length > 0 ? (
              <>
                <div className="wishlist-grid">
                  {wishlistItems.map((item) => (
                    <div key={item.id} className="wishlist-item-card">
                      {item.product.image ? (
                        <img
                          src={item.product.image.url}
                          alt={item.product.image.label || item.product.name}
                          className="wishlist-item-image"
                          loading="lazy"
                        />
                      ) : (
                        <div className="wishlist-item-image no-image-placeholder">No Image</div>
                      )}
                      <div className="wishlist-item-header">
                        <div className="wishlist-item-info">
                          <h4 className="wishlist-item-name">{item.product.name}</h4>
                          <div className="wishlist-item-price">
                            {item.product.price_range?.minimum_price?.final_price && (
                              <span className="price">
                                {item.product.price_range.minimum_price.final_price.value} {item.product.price_range.minimum_price.final_price.currency}
                              </span>
                            )}
                            {item.product.price_range?.minimum_price?.regular_price &&
                             item.product.price_range.minimum_price.regular_price.value !== item.product.price_range.minimum_price.final_price.value && (
                              <span className="regular-price">
                                {item.product.price_range.minimum_price.regular_price.value} {item.product.price_range.minimum_price.regular_price.currency}
                              </span>
                            )}
                          </div>
                          {item.quantity > 1 && (
                            <p className="wishlist-item-quantity">Quantity: {item.quantity}</p>
                          )}
                          {item.configurable_options && item.configurable_options.length > 0 && (
                            <div className="wishlist-item-options">
                              {item.configurable_options.map((option, index) => (
                                <p key={index} className="wishlist-item-option">
                                  {option.option_label}: {option.value_label}
                                </p>
                              ))}
                            </div>
                          )}
                          {item.added_at && (
                            <p className="wishlist-item-date">
                              Added: {new Date(item.added_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="wishlist-item-actions">
                        <button
                          className="btn-primary"
                          onClick={() => handleMoveToCart(wishlist.id, item)}
                        >
                          Move to Cart
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleRemoveFromWishlist(wishlist.id, item.id)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="empty-state">
                <p>Your wishlist is empty</p>
                <Link to="/" className="btn-primary">
                  Continue Shopping
                </Link>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountWishlist;